import React, { useState } from 'react';
import toast from 'react-hot-toast';
import {
  Mail,
  Phone,
  MapPin,
  Clock,
  Send,
  MessageSquare,
  HelpCircle,
  FileText,
  Users,
  CheckCircle,
} from 'lucide-react';
import { Card } from '@components/common/Card';
import { Input } from '@components/forms/Input';
import { Button } from '@components/common/Button';

export const Contact: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    category: 'general',
    message: '',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('📧 Mock: Contact form submitted', formData);
    toast.success("Message sent successfully! We'll get back to you within 24-48 hours.");
    // Reset form
    setFormData({
      name: '',
      email: '',
      phone: '',
      subject: '',
      category: 'general',
      message: '',
    });
  };

  const contactInfo = [
    {
      icon: <Phone className="h-6 w-6" />,
      title: 'Phone',
      details: ['+234 (0) 9 461 5000', '+234 (0) 9 461 5001'],
      subtext: 'Monday - Friday, 8:00 AM - 5:00 PM',
      color: 'blue',
    },
    {
      icon: <Mail className="h-6 w-6" />,
      title: 'Email',
      details: ['support@ncaa-dcev.gov.ng', 'licensing@ncaa.gov.ng'],
      subtext: "We'll respond within 24-48 hours",
      color: 'green',
    },
    {
      icon: <MapPin className="h-6 w-6" />,
      title: 'Head Office',
      details: ['Aviation House, Tafawa Balewa Way', 'Central Business District, Abuja, Nigeria'],
      subtext: 'Open to public Monday - Friday',
      color: 'purple',
    },
    {
      icon: <Clock className="h-6 w-6" />,
      title: 'Working Hours',
      details: ['Monday - Friday: 8:00 AM - 5:00 PM', 'Saturday: 9:00 AM - 2:00 PM'],
      subtext: 'Closed on Sundays and public holidays',
      color: 'orange',
    },
  ];

  const categories = [
    { value: 'general', label: 'General Inquiry', icon: <HelpCircle className="h-4 w-4" /> },
    { value: 'application', label: 'Application Support', icon: <FileText className="h-4 w-4" /> },
    { value: 'technical', label: 'Technical Issue', icon: <MessageSquare className="h-4 w-4" /> },
    { value: 'appointment', label: 'Appointment/Booking', icon: <Clock className="h-4 w-4" /> },
    {
      value: 'document',
      label: 'Document Verification',
      icon: <CheckCircle className="h-4 w-4" />,
    },
  ];

  const faqs = [
    {
      question: 'How long does it take to process my application?',
      answer:
        'Application processing typically takes 4-6 weeks from the date of submission with complete documentation.',
    },
    {
      question: 'Can I track my application status online?',
      answer:
        'Yes, you can track your application in real-time through your DCEV Portal dashboard.',
    },
    {
      question: 'What if I need to reschedule my appointment?',
      answer:
        'You can reschedule appointments through the portal or by contacting the testing center directly at least 24 hours in advance.',
    },
    {
      question: 'Are walk-in services available?',
      answer:
        'No, all services require prior booking through the DCEV Portal to ensure proper scheduling and service delivery.',
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-ncaa-primary to-blue-700 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Contact Us</h1>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto">
              Get in touch with NCAA support for assistance with your aviation licensing journey
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Contact Information Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {contactInfo.map((info, index) => (
            <Card key={index}>
              <div className="text-center">
                <div
                  className={`inline-flex items-center justify-center w-16 h-16 bg-${info.color}-100 rounded-full mb-4`}
                >
                  <div className={`text-${info.color}-600`}>{info.icon}</div>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{info.title}</h3>
                {info.details.map((detail, idx) => (
                  <p key={idx} className="text-sm text-gray-700 font-medium">
                    {detail}
                  </p>
                ))}
                <p className="text-xs text-gray-500 mt-2">{info.subtext}</p>
              </div>
            </Card>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
          {/* Contact Form */}
          <div className="lg:col-span-2">
            <Card>
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-ncaa-dark mb-2">Send Us a Message</h2>
                <p className="text-gray-600">
                  Fill out the form below and we'll get back to you as soon as possible
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    label="Full Name"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="John Doe"
                  />
                  <Input
                    label="Email Address"
                    type="email"
                    required
                    icon={<Mail className="h-4 w-4" />}
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="john@example.com"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    label="Phone Number"
                    type="tel"
                    icon={<Phone className="h-4 w-4" />}
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="+234 xxx xxxx xxx"
                  />
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                    <select
                      required
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ncaa-primary focus:border-transparent"
                    >
                      {categories.map((cat) => (
                        <option key={cat.value} value={cat.value}>
                          {cat.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <Input
                  label="Subject"
                  required
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  placeholder="Brief description of your inquiry"
                />

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Message <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    required
                    rows={6}
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    placeholder="Please provide details about your inquiry..."
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ncaa-primary focus:border-transparent resize-none"
                  />
                </div>

                <div className="flex items-center gap-4">
                  <Button type="submit" icon={<Send className="h-4 w-4" />}>
                    Send Message
                  </Button>
                  <p className="text-sm text-gray-500">We'll respond within 24-48 hours</p>
                </div>
              </form>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Quick Links */}
            <Card>
              <h3 className="text-lg font-bold text-gray-900 mb-4">Quick Links</h3>
              <div className="space-y-2">
                <a href="/about" className="block text-blue-600 hover:underline text-sm">
                  → About NCAA
                </a>
                <a href="/requirements" className="block text-blue-600 hover:underline text-sm">
                  → License Requirements
                </a>
                <a href="/centers" className="block text-blue-600 hover:underline text-sm">
                  → Capture Centers{' '}
                </a>
                <a href="/register" className="block text-blue-600 hover:underline text-sm">
                  → Create Account
                </a>
                <a href="/login" className="block text-blue-600 hover:underline text-sm">
                  → Sign In
                </a>
              </div>
            </Card>

            {/* Social Media */}
            <Card>
              <h3 className="text-lg font-bold text-gray-900 mb-4">Connect With Us</h3>
              <p className="text-sm text-gray-600 mb-4">
                Follow us on social media for updates, news, and announcements
              </p>
              <div className="flex gap-3">
                <a
                  href="https://twitter.com/NCAA_Nigeria"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center w-10 h-10 bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200 transition-colors"
                >
                  <Users className="h-5 w-5" />
                </a>
                <a
                  href="https://facebook.com/NCAA.Nigeria"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center w-10 h-10 bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200 transition-colors"
                >
                  <Users className="h-5 w-5" />
                </a>
                <a
                  href="https://linkedin.com/company/ncaa-nigeria"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center w-10 h-10 bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200 transition-colors"
                >
                  <Users className="h-5 w-5" />
                </a>
              </div>
            </Card>

            {/* Emergency Contact */}
            <Card className="bg-red-50 border-red-200">
              <h3 className="text-lg font-bold text-red-900 mb-2">Emergency Contact</h3>
              <p className="text-sm text-red-800 mb-3">For urgent safety or security matters</p>
              <a
                href="tel:+234911"
                className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 transition-colors"
              >
                <Phone className="h-4 w-4" />
                Call Emergency Line
              </a>
            </Card>
          </div>
        </div>

        {/* FAQs */}
        <Card className="mb-8">
          <h2 className="text-2xl font-bold text-ncaa-dark mb-6">Frequently Asked Questions</h2>
          <div className="space-y-6">
            {faqs.map((faq, index) => (
              <div key={index} className="border-b border-gray-200 pb-4 last:border-0">
                <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-start gap-2">
                  <HelpCircle className="h-5 w-5 text-ncaa-primary flex-shrink-0 mt-0.5" />
                  {faq.question}
                </h3>
                <p className="text-gray-600 ml-7">{faq.answer}</p>
              </div>
            ))}
          </div>
        </Card>

        {/* Office Locations Map Placeholder */}
        <Card>
          <h2 className="text-2xl font-bold text-ncaa-dark mb-4">Visit Our Offices</h2>
          <p className="text-gray-600 mb-6">
            NCAA has offices across Nigeria. Our main office is located in Abuja, with regional
            offices in major cities.
          </p>
          <div className="bg-gray-200 rounded-lg h-96 flex items-center justify-center">
            <div className="text-center">
              <MapPin className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">Interactive map would be integrated here</p>
              <a
                href="https://www.google.com/maps/search/NCAA+Nigeria"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 mt-4 px-6 py-2 bg-ncaa-primary text-white font-medium rounded-lg hover:bg-blue-700 transition-colors"
              >
                <MapPin className="h-4 w-4" />
                View on Google Maps
              </a>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};
